import ReactDOM from "react-dom";
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { PaperBG, PaperDarkBG, PaperGradientBG, UnderlineSketch } from "../kits";
import Kompass from "../components/Kompass";
import { Helmet } from "react-helmet";
import { aboutSchema } from "./utils/AboutSchema";
import BookingModal from "./BookingModal";
import ModalWrapper from "./utils/ModalWrapper";
import MonoButton from "../kits/DesignKit/MonoButton";
import HowItWorks_small from "../components/HowItWorks_small";
import SectionHeader from "./SectionHeader";
import { motion, useInView } from "framer-motion";
import { HashLink } from "react-router-hash-link";
import { FaCode, FaChalkboardTeacher, FaComments } from "react-icons/fa";
import TechStack from "./TechStack";
import rough from "roughjs/bundled/rough.esm.js";
import RoughBorder from "../kits/LayoutKit/RoughBorder";

// import Komponenten und Hooks

import ContactForm from "./ContactForm";


// Story-Kapitel Daten
const storyChapters = [
  {
    id: "01",
    title: "Der Anfang",
    year: "~2005",
    align: "right",
    content: `Mit 25, mitten im Wirtschaftsinformatik-Studium, kam eine Anfrage: "Kannst du eine Software bauen, mit der Kunden interaktiv Häuser konfigurieren können – inklusive Inneneinrichtung?"`,
    highlight: `Ich sagte ja. Ahnung hatte ich keine.`,
    conclusion: `4 Monate später lief die Software. Das war der Moment, in dem ich verstand: Ich löse Probleme, indem ich entwickle.`,
  },
  {
    id: "02",
    title: "Der Umweg",
    year: "~2010",
    align: "left",
    content: `Nach dem Studium: Klassische Unternehmensberatung für Hardware und Netzwerktechnik. Zu viel interne Strukturen, zu wenig echte Wirkung.`,
    highlight: `Ich wollte nicht optimieren – ich wollte erschaffen.`,
    conclusion: `Die Idee für meine Doktorarbeit: Server-Abwärme als Heizungsluft nutzen. Anders denken war schon immer mein Ding.`,
  },
  {
    id: "03",
    title: "Die kreative Phase",
    year: "~2012",
    align: "right",
    content: `Dann kamen Jahre in Film und Animation. Das Polygon war immer meine Freude – 3D-Modelle, Animationen, visuelle Welten erschaffen.`,
    highlight: `Dabei entdeckte ich Python-Scripting.`,
    conclusion: `Kleine Plugins, die Workflows optimierten. Design und Code – das war keine Entscheidung, sondern eine Erkenntnis.`,
  },
  {
    id: "04",
    title: "Der Lehrende",
    year: "~2015",
    align: "left",
    content: `An der Uni: GameDesign-Dozent für Unity und Maya. Nicht "wie geht die Software" – sondern "wie erreichst du dein Ziel".`,
    highlight: `Befähigen statt nur Abliefern.`,
    conclusion: `Diese Tätigkeit mache ich bis heute. Weil ich glaube: Wissen, das geteilt wird, wächst.`,
  },
  {
    id: "05",
    title: "Heute",
    year: "jetzt",
    align: "center",
    content: `Zwischendurch immer wieder Projekte für Telekom, adidas, Siemens und andere – reale Herausforderungen, virtuelle Modelle, three.js, React, Flutter.`,
    highlight: `So bleibe ich: Design + Code. Dozent + Entwickler.`,
    conclusion: `Nicht entweder oder. Sondern beides. Weil echte Lösungen beides brauchen.`,
  },
];

// Einfacher handgezeichneter Kreis (für Profilbild etc.)
function RoughCircle({ size = 128, color = "#AC8E66", strokeWidth = 1.5, roughness = 0.8 }) {
  const svgRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useLayoutEffect(() => {
    if (!isClient || !svgRef.current) return;
    svgRef.current.innerHTML = "";
    const rc = rough.svg(svgRef.current);
    const padding = strokeWidth + 2;
    const node = rc.circle(size / 2, size / 2, size - padding * 2, {
      roughness,
      stroke: color,
      strokeWidth,
      fill: "none",
    });
    svgRef.current.appendChild(node);
  }, [isClient, size, color, strokeWidth, roughness]);

  if (!isClient) return null;

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      className="pointer-events-none"
    />
  );
}

// Kapitel-Button mit RoughBorder-Kreis
function ChapterCircle({ number, year, isActive = true, size = 80 }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useLayoutEffect(() => {
    if (!isClient || !svgRef.current) return;
    svgRef.current.innerHTML = "";
    const rc = rough.svg(svgRef.current);
    const padding = 4;
    const node = rc.circle(size / 2, size / 2, size - padding * 2, {
      roughness: 0.8,
      stroke: isActive ? "#AC8E66" : "#d1d5db",
      strokeWidth: 1.2,
      fill: "none",
    });
    svgRef.current.appendChild(node);
  }, [isClient, size, isActive]);

  if (!isClient) return null;

  return (
    <div
      ref={containerRef}
      className="relative flex items-center justify-center rounded-full bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7] shadow-md"
      style={{ width: size, height: size }}
    >
      {/* RoughBorder SVG */}
      <svg
        ref={svgRef}
        width={size}
        height={size}
        className="absolute inset-0 pointer-events-none"
      />
      {/* Inhalt: Nummer + Jahr */}
      <div className="relative z-10 flex flex-col items-center justify-center">
        <span className={`font-mono text-lg md:text-xl font-bold transition-colors duration-500 ${isActive ? "text-brand" : "text-gray-400"}`}>
          {number}
        </span>
        <span className={`text-[10px] font-mono transition-colors duration-500 ${isActive ? "text-gray-500" : "text-gray-300"}`}>
          {year}
        </span>
      </div>
    </div>
  );
}

// Einzelnes Kapitel mit Scroll-Animation
function StoryChapter({ chapter, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const isLeft = chapter.align === "left";
  const isCenter = chapter.align === "center";

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay: 0.1 }}
      className={`relative py-12 md:py-16 ${isCenter ? "text-center" : ""}`}
    >
      <div className={`max-w-4xl mx-auto px-6 ${!isCenter ? "md:flex md:items-start md:gap-12" : ""}`}>

        {/* Kapitel-Nummer mit Jahr im Kreis */}
        {!isCenter && (
          <div className={`flex-shrink-0 mb-6 md:mb-0 flex justify-center md:justify-start ${isLeft ? "md:order-2" : "md:order-1"}`}>
            <ChapterCircle number={chapter.id} year={chapter.year} isActive={isInView} size={80} />
          </div>
        )}

        {/* Content */}
        <div className={`flex-1 text-center ${isLeft ? "md:order-1 md:text-right" : isCenter ? "" : "md:order-2 md:text-left"} ${isCenter ? "max-w-2xl mx-auto" : ""}`}>

          {/* Kapitel-Titel */}
          <h3 className={`text-sm md:text-base font-mono font-semibold text-gray-900 mb-4`}>
            {isCenter && (
              <div className="flex flex-col items-center mb-4">
                <ChapterCircle number={chapter.id} year={chapter.year} isActive={true} size={96} />
              </div>
            )}
            {chapter.title}
          </h3>

          {/* Story-Text */}
          <p className="text-xs md:text-sm text-gray-700 leading-relaxed mb-4 font-mono">
            {chapter.content}
          </p>

          {/* Highlight */}
          <p className="text-sm md:text-base text-brand font-semibold mb-4 font-mono">
            {chapter.highlight}
          </p>

          {/* Conclusion */}
          <p className="text-xs md:text-sm text-gray-600 leading-relaxed font-mono">
            {chapter.conclusion}
          </p>
        </div>

      </div>
    </motion.div>
  );
}

const SectionBadge = ({ label }) => (
  <div className="flex justify-center mb-3">
    <span className="inline-flex items-center gap-2 rounded-full border border-brand bg-transparent px-4 py-1 text-[10px] uppercase tracking-[0.4em] font-mono font-semibold text-brand">
      <span className="h-2.5 w-2.5 rounded-full bg-brand" />
      {label}
    </span>
  </div>
);

const About = () => {
  const [showBooking, setShowBooking] = useState(false);
  const location = useLocation();
  const introRef = useRef(null);
  const introInView = useInView(introRef, { once: true });
  const developmentRef = useRef(null);
  const trainingRef = useRef(null);
  const consultingRef = useRef(null);
  const pillars = [
    {
      id: "entwicklung",
      title: "Entwicklung",
      icon: FaCode,
      highlight: "React · Angular · Flutter · three.js · System Architecture",
      detail: "Von der Idee zur skalierbaren Lösung",
      ref: developmentRef,
      delay: 0.1,
    },
    {
      id: "training",
      title: "Training",
      icon: FaChalkboardTeacher,
      highlight: "Unity · Maya · Web-Technologien · Team-Workshops",
      detail: "Wissen, das im Alltag wirkt",
      ref: trainingRef,
      delay: 0.2,
    },
    {
      id: "beratung",
      title: "Beratung",
      icon: FaComments,
      highlight: "Architektur-Reviews · Code-Audits · Strategie",
      detail: "Klarheit für komplexe Entscheidungen",
      ref: consultingRef,
      delay: 0.3,
    },
  ];

  const handleOpenBooking = () => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setShowBooking(true);
  };

  useEffect(() => {
    if (location.hash !== "#referenzen") return;
    let tries = 0;
    const tryScroll = () => {
      const el = document.getElementById("referenzen");
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY + 150;
        window.scrollTo({ top, behavior: "smooth" });
        return;
      }
      tries += 1;
      if (tries < 20) {
        setTimeout(tryScroll, 50);
      }
    };
    tryScroll();
  }, [location.hash]);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      </Helmet>

      {/* ========== INTRO - Der Mensch ========== */}
      <PaperGradientBG 
      variant="orange-blue-dark"
      
      >
        
      <section className="relative text-white overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28">
          <motion.div
            ref={introRef}
            initial={{ opacity: 0, y: 10 }}
            animate={introInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 500 }}
            transition={{ duration: 0.9 }}
            className="text-center"
          >
            {/* Foto/Avatar */}
            <div className="relative w-32 h-32 md:w-40 md:h-40 mx-auto mb-8">
              {/* RoughCircle für Mobile */}
              <div className="absolute inset-0 flex items-center justify-center md:hidden">
                <RoughCircle size={128} color="#AC8E66" strokeWidth={1.5} roughness={0.8} />
              </div>
              {/* RoughCircle für Desktop */}
              <div className="absolute inset-0 items-center justify-center hidden md:flex">
                <RoughCircle size={160} color="#AC8E66" strokeWidth={1.5} roughness={0.8} />
              </div>
              <div className="absolute inset-2 rounded-full bg-gradient-to-br from-brand to-brand flex items-center justify-center overflow-hidden">
                <img
                  src="/images/den.png"
                  width={256}
                  height={256}
                  alt="Denis Bitter"
                  className="w-full h-full object-cover object-[80%_20%]"
                />
              </div>
            </div>

            {/* Greeting */}
            <h1 className="text-2xl md:text-4xl text-amber-100 font-bold font-mono mb-4">
              Moin, ich bin <span className="text-brand">Denis</span>
            </h1>

            <p className="text-sm md:text-base text-amber-50 max-w-xl mx-auto leading-relaxed font-mono">
              Software Architect, Full-Stack Entwickler und Dozent aus Hamburg.
              <br className="hidden md:block" />
              Ich glaube daran, dass gute Technologie verstanden werden muss – nicht nur funktionieren.
            </p>

            {/* Scroll-Hint */}
            <div className="mt-12 animate-bounce">
              <span className="text-xs text-brand font-mono">↓ Meine Geschichte</span>
            </div>
          </motion.div>
        </div>
      </section>
      </PaperGradientBG>

      {/* ========== KAPITEL - Die Story ========== */}
      <PaperBG>
        <div className="py-12 md:py-20">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-xs text-brand uppercase tracking-wider font-mono mb-2">Mein Weg</p>
            <h2 className="text-lg md:text-xl font-mono font-bold text-gray-900">
              Keine gerade Linie – jedoch ein klarer Kurs.
            </h2>
          </div>

          {/* Story Chapters */}
          <div className="divide-y divide-brand">
            {storyChapters.map((chapter, index) => (
              <StoryChapter key={chapter.id} chapter={chapter} index={index} />
            ))}
          </div>
        </div>
      </PaperBG>

      {/* ========== WAS ICH HEUTE TUE ========== */}
       <PaperGradientBG variant="orange-blue-dark">
      <section className="relative text-slate-100 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <SectionBadge label="Heute" />
            <h2 className="text-xl md:text-2xl font-bold font-mono text-brand">
              Drei Säulen. <span className="text-brand">Ein Ziel.</span>
            </h2>
            <p className="text-sm text-amber-100 mt-4 font-mono">
              Software, die funktioniert UND verstanden wird.
            </p>
          </div>

          {/* Drei Säulen */}
           

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {pillars.map(({ id, title, icon: Icon, highlight, detail, ref, delay }) => (
              <motion.div
                key={id}
                ref={ref}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay }}
                viewport={{ once: true }}
                className="relative   rounded-xl p-6 text-center overflow-hidden"
              >
                <div className="relative z-10">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full border border-brand flex items-center justify-center">
                    <Icon className="text-brand text-xl" />
                  </div>
                  <h3 className="text-sm font-semibold font-mono text-brand mb-2">{title}</h3>
                  <p className="text-xs text-amber-100 font-mono leading-relaxed">
                    {highlight}
                  </p>
                  <p className="text-xs text-brand mt-3 font-mono">
                    {detail}
                  </p>
                </div>
                <RoughBorder
                  parentRef={ref}
                  radius={24}
                  padding={4}
                  color="#AC8E66"
                  strokeWidth={0.35}
                  roughness={1}
                  bowing={1.3}
                />
              </motion.div>
            ))}
          </div>
        </div>
       
      </section>
      </PaperGradientBG>

      {/* ========== TECH STACK ========== */}
      <PaperBG>
        <section className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center mb-10">
            <SectionBadge label="Tech Stack" />
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 font-mono">Womit ich arbeite</h2>
          </div>

          <TechStack extended />
        </section>
      </PaperBG>

      {/* ========== REFERENZEN ========== */}
      <PaperGradientBG variant="orange-blue-dark">
        <section id="referenzen" className="max-w-4xl mx-auto px-4 py-16 space-y-8">
          <div className="text-center">
            <SectionBadge label="Referenzen" />
            <h2 className="text-xl sm:text-2xl font-bold text-amber-100 mb-2">Was andere sagen</h2>
            <p className="text-sm text-brand">Stimmen aus echten Projekten + Training + Zusammenarbeit.</p>
          </div>

          <div className="">
            <HowItWorks_small />
          </div>
        </section>
      </PaperGradientBG>

      {/* ========== KOMPASS ========== */}
      <PaperBG>
        <section id="kompass" className="max-w-5xl mx-auto px-6 pt-0 pb-16 space-y-6">
          <SectionHeader
            badge="Kompass"
            headline="Woran ich glaube"
            subtext="Prinzipien, die mich leiten – in jedem Projekt."
          />
          <Kompass />
        </section>
      </PaperBG>

      {/* ========== CTA ========== */}

      <PaperGradientBG>
       
      <section className="relative  text-white py-16 md:py-20">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-xl md:text-2xl text-brand font-bold font-mono mb-4">
            Klingt interessant?
          </h2>
          <p className="text-sm text-amber-100 font-mono mb-8">
            Lass uns reden – über dein Projekt, deine Herausforderung oder einfach so.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-full sm:gap-6">
            <HashLink smooth to="/contact">
              <MonoButton
                className="px-8 py-3 text-sm border-brand bg-slate-800 w-full sm:w-auto"
                textColor="text-brand"
                underlineColors="ZenBrand"
              >
                Kontakt aufnehmen
              </MonoButton>
            </HashLink>
            <MonoButton
              onClick={() => setShowBooking(true)}
              className="px-8 py-3 text-sm w-full sm:w-auto border bg-slate-800 border-brand hover:shadow-brand"
              textColor="text-brand"
              underlineColors="ZenBrand"
            >
              Lern mich kennen
            </MonoButton>
          </div>
        </div>
      </section>

                {/* Kontaktformular */}
          <div className="max-w-3xl mx-auto mt-10 px-4 pb-16">
            <ContactForm />
         

         
 </div>
      </PaperGradientBG>

      {/* Modal */}
      {showBooking &&
        ReactDOM.createPortal(
          <ModalWrapper onClose={() => setShowBooking(false)}>
            <BookingModal onClose={() => setShowBooking(false)} />
          </ModalWrapper>,
          document.getElementById("modal-root")
        )
      }

    </>
  );
};

export default About;
